import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { hasPermission } from "./permissions";
import UserRoleChip from "../components/UserRoleChip";

const AuthenticatedLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState("");
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const navigationRef = useRef(null);

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (!navigationRef.current?.contains(event.target)) {
        setIsNavigationOpen(false);
      }
      document.querySelectorAll("details[open]").forEach((details) => {
        if (!details.contains(event.target)) details.open = false;
      });
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, []);

  const handleLogout = async () => {
    setError("");
    setLoggingOut(true);

    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      setError("Unable to log out. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <>
      <div ref={navigationRef} className="relative z-50">
        <header className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 sm:px-6">
          {!user?.mustChangePassword && (
            <button
              type="button"
              aria-label={
                isNavigationOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={isNavigationOpen}
              aria-controls="main-navigation"
              onClick={() => setIsNavigationOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center rounded-md text-zinc-700 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-400 lg:hidden"
            >
              <svg
                aria-hidden="true"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                {isNavigationOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
          {user?.mustChangePassword ? (
            <div className="mr-auto flex items-center gap-3 text-sm font-semibold text-amber-800">
              <img src="/logos/J-Starter.png" alt="" className="h-8 w-auto" />
              Password change required
            </div>
          ) : (
            <NavLink
              to="/"
              className="hidden items-center gap-3 text-lg font-semibold text-zinc-900 lg:flex"
            >
              <img src="/logos/J-Starter.png" alt="" className="h-8 w-auto" />
              J-Starter Portal
            </NavLink>
          )}
          {!user?.mustChangePassword && (
            <nav
              aria-label="Main navigation"
              className="ml-8 mr-auto hidden items-center gap-1 lg:flex"
            >
              {[
                { label: "Home", to: "/", end: true },
                { label: "Starters", to: "/starters" },
                { label: "Armatures", to: "/armatures" },
                { label: "Field Cases", to: "/field-cases" },
              ].map(({ label, to, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium ${
                      isActive
                        ? "bg-zinc-100 text-zinc-950"
                        : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
              {hasPermission(user, "API_DOCS_READ") && (
                <details className="group relative ml-2">
                  <summary className="flex cursor-pointer list-none items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950 focus:outline-none focus:ring-2 focus:ring-zinc-300">
                    API docs
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-4 w-4 transition-transform group-open:rotate-180"
                    >
                      <path
                        d="m5 7.5 5 5 5-5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </summary>
                  <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-lg border border-zinc-200 bg-white p-1 shadow-lg">
                    <a
                      href="/swagger-ui.html"
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-md px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                    >
                      Swagger UI
                    </a>
                    <a
                      href="/v3/api-docs"
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-md px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                    >
                      OpenAPI JSON
                    </a>
                  </div>
                </details>
              )}
            </nav>
          )}
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {!user?.mustChangePassword && (
              <NavLink
                to="/account"
                aria-label={`Account for ${user?.email || "current user"}`}
                className={({ isActive }) =>
                  `flex min-w-0 items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors sm:gap-3 sm:px-3 ${
                    isActive
                      ? "border-blue-200 bg-blue-50"
                      : "border-transparent hover:border-zinc-200 hover:bg-zinc-50"
                  }`
                }
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold uppercase text-blue-900"
                >
                  {user?.email?.slice(0, 1) || "U"}
                </span>
                <span className="flex min-w-0 flex-col items-start gap-1">
                  <span
                    title={user?.email}
                    className="max-w-[30vw] truncate text-sm font-medium leading-4 text-zinc-900 sm:max-w-52"
                  >
                    {user?.email}
                  </span>
                  <span className="hidden sm:flex">
                    <UserRoleChip
                      role={user?.role}
                      title={`Your role: ${user?.role || "unknown"}`}
                    />
                  </span>
                </span>
              </NavLink>
            )}
            <button
              type="button"
              onClick={handleLogout}
              disabled={loggingOut}
              className="shrink-0 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loggingOut ? "Logging out…" : "Log out"}
            </button>
          </div>
        </header>

        {isNavigationOpen && !user?.mustChangePassword && (
          <nav
            id="main-navigation"
            aria-label="Main navigation"
            className="absolute left-4 top-full z-50 w-max max-w-[calc(100vw-2rem)] border border-zinc-200 bg-white px-4 py-3 shadow-lg"
          >
            <div className="flex flex-col gap-1">
              {[
                { label: "Home", to: "/", end: true },
                {
                  label: "Starters",
                  to: "/starters",
                  icon: "/icons/engine-icon.svg",
                },
                {
                  label: "Armatures",
                  to: "/armatures",
                  icon: "/icons/electric-engine-icon.svg",
                },
                {
                  label: "Field Cases",
                  to: "/field-cases",
                },
              ].map(({ label, to, end, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setIsNavigationOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-zinc-100 text-zinc-900"
                        : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  {label === "Home" && (
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m3 10 9-7 9 7" />
                      <path d="M5 9v12h14V9M9 21v-7h6v7" />
                    </svg>
                  )}
                  {icon && (
                    <img
                      src={icon}
                      alt=""
                      aria-hidden="true"
                      className="h-5 w-5 shrink-0 grayscale"
                    />
                  )}
                  {label}
                </NavLink>
              ))}
              {hasPermission(user, "API_DOCS_READ") && (
                <>
                  <div
                    role="separator"
                    aria-orientation="horizontal"
                    className="my-2 border-t border-zinc-200"
                  />
                  <a
                    href="/swagger-ui.html"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsNavigationOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-normal text-zinc-700 transition-colors hover:bg-zinc-100"
                  >
                    <img
                      src="/icons/swagger-ui-icon.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-5 w-5 shrink-0 grayscale"
                    />
                    Swagger UI
                  </a>
                  <a
                    href="/v3/api-docs"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsNavigationOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-normal text-zinc-700 transition-colors hover:bg-zinc-100"
                  >
                    <img
                      src="/icons/api-docs-icon.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-5 w-5 shrink-0 grayscale"
                    />
                    API docs
                  </a>
                </>
              )}
            </div>
            <NavLink
              to="/account"
              onClick={() => setIsNavigationOpen(false)}
              className="mt-3 block break-all border-t border-zinc-100 pt-3 text-sm text-zinc-600 hover:text-zinc-900"
            >
              {user.email}
            </NavLink>
          </nav>
        )}
      </div>

      {error && (
        <p
          className="bg-red-100 px-2 py-2 text-sm text-red-800 sm:px-3"
          role="alert"
        >
          {error}
        </p>
      )}

      {children}
    </>
  );
};

export default AuthenticatedLayout;
